import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, CheckCircle2, ArrowRight, Calendar, Image } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/layout/Layout';
import { pmkvyInfo, partnerLogos } from '../data/mock';

const projectGallery = [
  {
    title: "PMKVY Training Session",
    image: "https://images.unsplash.com/photo-1756885375569-f04400d99cee?w=600&q=80"
  },
  {
    title: "Apprenticeship Enrollment Camp",
    image: "https://images.unsplash.com/photo-1758522275018-2751894b49c8?w=600&q=80"
  },
  {
    title: "Skill Development Workshop",
    image: "https://images.unsplash.com/photo-1759523146335-0069847ceb16?w=600&q=80"
  },
  {
    title: "Technical Training Program",
    image: "https://images.unsplash.com/photo-1690356107486-0796de806f63?w=600&q=80"
  },
  {
    title: "Industry Visit",
    image: "https://images.pexels.com/photos/4491910/pexels-photo-4491910.jpeg?w=600"
  },
  {
    title: "Placement Drive",
    image: "https://images.unsplash.com/photo-1758522275031-70ea3febfa2e?w=600&q=80"
  }
];

const affiliations = [
  {
    name: "NATS - Under MoE",
    description: "National Apprenticeship Training Scheme under Ministry of Education for graduate/diploma apprentices",
    logo: partnerLogos.nats
  },
  {
    name: "NAPS - Under MSDE",
    description: "National Apprenticeship Promotion Scheme under Ministry of Skill Development & Entrepreneurship",
    logo: partnerLogos.naps
  },
  {
    name: "Skill India Digital Hub (SIDH)",
    description: "Registered Training Partner on the official Skill India Portal for quality training delivery",
    logo: partnerLogos.sidh
  },
  {
    name: "Telecom Sector Skill Council",
    description: "Affiliated for specialized telecom sector training and certification programs",
    logo: partnerLogos.tssc
  }
];

const ProjectsPage = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-700 to-slate-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Our Projects</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-2 mb-6">
              Flagship Programs & Initiatives
            </h1>
            <p className="text-xl text-blue-200 leading-relaxed">
              Discover our major skill development programs and achievements across India.
            </p>
          </div>
        </div>
      </section>

      {/* PMKVY Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <img src={partnerLogos.pmkvy} alt="PMKVY" className="h-16 bg-white p-2 rounded-lg shadow" />
                <img src={partnerLogos.skillIndia} alt="Skill India" className="h-16 bg-white p-2 rounded-lg shadow" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">{pmkvyInfo.title}</h2>
              <p className="text-slate-600 leading-relaxed mb-6">{pmkvyInfo.description}</p>
              
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <Award className="w-6 h-6 text-emerald-600" />
                  <span className="font-bold text-emerald-800">Our Achievement</span>
                </div>
                <p className="text-emerald-700">{pmkvyInfo.achievement}</p>
              </div>
              
              <h3 className="font-semibold text-slate-900 mb-3">Program Highlights</h3>
              <ul className="space-y-2">
                {pmkvyInfo.highlights.map((highlight, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-600">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                    {highlight}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1756885375569-f04400d99cee?w=800&q=80"
                alt="PMKVY Training"
                className="w-full h-96 object-cover rounded-2xl shadow-lg"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-sm rounded-xl p-4">
                <p className="text-slate-900 font-semibold">Pradhan Mantri Kaushal Vikas Yojana</p>
                <p className="text-slate-600 text-sm">Government's flagship skill development initiative</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Affiliations */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Affiliations</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2">
              Other Program Affiliations
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {affiliations.map((affiliation, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow"
              >
                <img
                  src={affiliation.logo}
                  alt={affiliation.name}
                  className="h-20 mb-6 bg-slate-50 p-2 rounded-lg"
                />
                <h3 className="text-xl font-bold text-slate-900 mb-2">{affiliation.name}</h3>
                <p className="text-slate-600">{affiliation.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Timeline */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Milestones</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2">
              Project Timeline
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { year: "2017-2019", title: "Foundation Phase", description: "Established core programs and obtained initial certifications" },
              { year: "2020-2022", title: "Growth Phase", description: "Expanded to 25+ establishments and trained 15,000+ candidates" },
              { year: "2023-Present", title: "Scale Phase", description: "Targeting 50 new partnerships and 50,000 candidate placements" }
            ].map((phase, index) => (
              <div
                key={index}
                className="relative bg-white rounded-xl p-6 shadow-md border-t-4 border-blue-700"
              >
                <div className="flex items-center gap-2 text-blue-700 font-bold mb-3">
                  <Calendar className="w-5 h-5" />
                  <span>{phase.year}</span>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{phase.title}</h3>
                <p className="text-slate-600 text-sm">{phase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Gallery</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
              Project Gallery
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Glimpses of our skill development programs and training sessions across India.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {projectGallery.map((item, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-xl aspect-square"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                  <div className="flex items-center gap-2 text-white">
                    <Image className="w-4 h-4" />
                    <span className="font-medium">{item.title}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Partners Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Our Network</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
              Industry Partners
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Collaborating with 37+ establishments across India for apprenticeship programs.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <img
              src={partnerLogos.industryPartners}
              alt="Industry Partners"
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-700 to-slate-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Users className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Be Part of Our Next Project
          </h2>
          <p className="text-blue-200 text-lg mb-8">
            Join us as a candidate or establishment partner and contribute to India's skill revolution.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/careers">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                Register as Candidate
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/apprenticeship">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900">
                Partner with Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ProjectsPage;
